import {useRef, useState} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {DescargarEvidencia} from '@/components/descargar-evidencia'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {PageHeading} from '@/components/page-heading'
import {ListEmptyState, SectionCard} from '@/components/section-card'
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_BYTES,
  evidenceTooBig,
  evidenceKindLabel,
  formatSize,
  uploadToStorage,
} from '@/hooks/sgsst/evidencias/use-evidence'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  mintMemberUploadTarget,
  rutaDeMiAporte,
  useAportarSolicitud,
  useMySolicitudes,
  type MemberSolicitud,
} from '@/hooks/sgsst/evidencias/use-solicitudes'
import {useBusiness} from '@/hooks/empresa/organizacion/use-businesses'
import {translateApiError} from '@/lib/errors'
import {formatDate, formatDateOnly} from '@/lib/risk'

const STATE_TONE = {
  pendiente: 'neutral',
  vencida: 'red',
  cumplida: 'green',
} as const

function FulfillUpload({businessId, solicitudId, label}: {businessId: string; solicitudId: string; label: string}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const inputRef = useRef<HTMLInputElement>(null)
  const aportar = useAportarSolicitud(businessId)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function start(files: FileList | null) {
    if (godReadOnly) return
    if (!files || files.length === 0) return
    const file = files[0]
    setError(null)
    if (!EVIDENCE_ALLOWED_TYPES.has(file.type)) return setError(t('errors.evidenceBadFormat'))
    if (file.size > EVIDENCE_MAX_BYTES) return setError(evidenceTooBig(t, file))
    try {
      setProgress(0)
      const target = await mintMemberUploadTarget(businessId, solicitudId, file)
      await uploadToStorage(target, file, setProgress)
      await aportar.mutateAsync({solicitudId, objectKey: target.object_key, file})
      setProgress(null)
    } catch (err) {
      setProgress(null)
      setError(translateApiError(err))
    }
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="space-y-1.5">
      <input
        ref={inputRef}
        type="file"
        accept={EVIDENCE_ACCEPT}
        hidden
        data-testid={`fulfill-input-${solicitudId}`}
        onChange={(event) => start(event.target.files)}
      />
      {progress !== null ? (
        <div className="h-1.5 w-40 overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-primary transition-all" style={{width: `${progress}%`}} />
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          data-testid={`fulfill-${solicitudId}`}
          disabled={aportar.isPending || godReadOnly}
          onClick={() => inputRef.current?.click()}>
          {label}
        </Button>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

function SolicitudCard({businessId, solicitud}: {businessId: string; solicitud: MemberSolicitud}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const dateText =
    solicitud.state === 'cumplida'
      ? t('solicitudes.fulfilledOn', {date: formatDate(solicitud.fulfilled!.uploaded_at)})
      : solicitud.state === 'vencida'
        ? t('solicitudes.overdueOn', {date: formatDateOnly(solicitud.due_date)})
        : t('solicitudes.dueOn', {date: formatDateOnly(solicitud.due_date)})

  return (
    <SectionCard
      testId="mi-solicitud-card"
      title={
        <span className="flex flex-wrap items-baseline gap-2">
          <span className="font-mono text-xs font-medium text-primary">{solicitud.standard_code}</span>
          <span className="text-base">{solicitud.standard_name}</span>
        </span>
      }
      subtitle={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="mi-solicitud-motivo">
          {t(solicitud.person_scoped ? 'solicitudes.motivo.personScopedMine' : 'solicitudes.motivo.tareaMine')}
          {solicitud.soporte_titulo && (
            <span data-testid="mi-solicitud-casilla">
              <Tag tone="neutral">{solicitud.soporte_titulo}</Tag>
            </span>
          )}
        </span>
      }
      actions={<Tag tone={STATE_TONE[solicitud.state]}>{t(`solicitudes.state.${solicitud.state}`)}</Tag>}>
      <p className="text-sm text-foreground">«{solicitud.note}»</p>
      <p className="mt-1.5 font-mono text-[11px] text-muted-foreground">{dateText}</p>
      {solicitud.state === 'vencida' && !solicitud.fulfilled && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="mi-solicitud-overdue-hint">
          {t('solicitudes.overdueStillCanUpload')}
        </p>
      )}
      <div className="mt-4">
        {solicitud.fulfilled ? (
          <div className="surface-divider flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2.5">
            <span className="rounded-sm bg-secondary px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
              {evidenceKindLabel(solicitud.fulfilled.content_type)}
            </span>
            <DescargarEvidencia
              ruta={rutaDeMiAporte(businessId, solicitud.fulfilled.id)}
              reservado={solicitud.fulfilled.de_salud && godReadOnly}
              className="min-w-0 flex-1 basis-40 break-all text-xs font-semibold text-foreground hover:text-primary">
              {solicitud.fulfilled.filename}
            </DescargarEvidencia>
            <span className="font-mono text-[10px] text-muted-foreground">
              {formatSize(solicitud.fulfilled.size_bytes)}
            </span>
            <FulfillUpload businessId={businessId} solicitudId={solicitud.id} label={t('solicitudes.replace')} />
          </div>
        ) : (
          <FulfillUpload businessId={businessId} solicitudId={solicitud.id} label={t('solicitudes.uploadSoporte')} />
        )}
      </div>
    </SectionCard>
  )
}

export function MiSolicitudesBody({businessId}: {businessId: string | undefined}) {
  const {t} = useTranslation()
  const {data, isLoading} = useMySolicitudes(businessId)
  const {data: business} = useBusiness(businessId)

  return (
    <>
      {business?.ai_validate_aportes && (
        <p className="mb-4 text-xs text-muted-foreground" data-testid="ai-employee-note">
          {t('validation.employeeNote')}
        </p>
      )}
      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : !data || data.items.length === 0 ? (
        <div className="surface mx-auto max-w-2xl">
          <ListEmptyState page title={t('solicitudes.mine.emptyTitle')} body={t('solicitudes.mine.emptyBody')} />
        </div>
      ) : (
        <div className="space-y-4">
          {data.items.map((solicitud) => (
            <SolicitudCard key={solicitud.id} businessId={businessId!} solicitud={solicitud} />
          ))}
        </div>
      )}
    </>
  )
}

/** Ruta suelta conservada para los enlaces guardados; mismo contenido que la pestaña «Solicitudes». */
export function MiSolicitudesPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()

  return (
    <div className="space-y-5">
      <PageHeading title={t('solicitudes.mine.title')} subtitle={t('solicitudes.mine.subtitle')} />
      <MiSolicitudesBody businessId={businessId} />
    </div>
  )
}
